import _ from "lodash";
import {FormattedMessage, useIntl} from "react-intl";
import {useContext, useEffect, useRef, useState} from "react";
import {License} from "../../../model/entitlement/License";
import {isAboutToExpire, isScheduled, isValid} from "../../../util/objectUtil";
import {TableColumns} from "./table/table-view";
import View, {MyLicensesProps as _MyLicensesProps, UILicense, UILicenseStatus} from "./my-licenses-view";
import {getAppBase} from "../../../util/env";
import UIConfiguration from "../../../ui-configuration/configuration-provider";
import TooltipTrigger from "../../components/tooltip-trigger";
import dayjs from "dayjs";
/**
 * Internal "state"
 */
let licenseCategories: string[]|undefined;
/**
 * Converts a License to UILicense for the table renderer
 */
function licenseToUILicense(
    l: License,
    ind:number,
    additionalLicenseData?:{
      name: string,
      displayName: string,
      category: string,
      [link:string]: string,
    }[],
    deltaMs?: number): UILicense {
  let additionalColumn: string|undefined;
  let additional: {
    name: string,
    displayName: string,
    category: string,
    [link:string]: string,
  } | undefined = undefined;
  if (additionalLicenseData) {
    additional = additionalLicenseData.find((la) => la.name === l.licensedItem?.name);
    // TODO: enable real above, disable mock below
    // additional = additionalLicenseData[ind*3];
  }
  let {
    name: string,
    displayName,
    category,
    ...links
  } = additional || {};
  if (!links) {
    links = {};
  } else {
    additionalColumn = Object.keys(links).filter((f,i,a) => a.indexOf(f) === i)[0];
  }
  const nowTime = new Date().getTime();
  let status:UILicenseStatus;
  if (!l.active) {
    status = UILicenseStatus.DISABLED;
  } else {
    if (isValid(l)) {
      if (isAboutToExpire(l,deltaMs)) {
        status = UILicenseStatus.EXPIRING;
      } else {
        status = UILicenseStatus.VALID;
      }
    } else {

      if (isScheduled(l)) {
        status = UILicenseStatus.SCHEDULED;
      } else if (l.validUntil && new Date(l.validUntil).getTime() < nowTime) {
        status = UILicenseStatus.EXPIRED;
      } else {
        status = UILicenseStatus.INVALID;
      }
    }
  }
  const retVal:UILicense = {
    id: l.id as string,
    name: displayName
        || l.licensedItem?.displayName
        || l.licensedItem?.name
        || undefined,
    status: status,
    isValid: l.active === true && isValid(l),
    category: category
        || undefined,
    validFrom: l.validFrom,
    validUntil: l.validUntil,
  };

  if (additionalColumn) {
    // Populate additional column as link with column name as type for custom translation
    retVal.link = [
      (links as { [key: string]: string })[additionalColumn] as string,
      additionalColumn,
      ];
  }
  return retVal;
}
export interface MyLicensesProps extends Omit<_MyLicensesProps, "intl"|"licenses"|"showAll"|"onSetShowAll"|"columnRenderers"|"categories"|"rowClassResolver"> {
  licenses: License[]|undefined;
}
function MyLicenses(props: MyLicensesProps) {
  const {
    userId,
    licenses,
    onLoadLicenses,
    isReady,
      ...other
  } = props;
  const intl = useIntl();
  const [additionalLicenseData, setAdditionalLicenseData] = useState<{
    name: string,
    displayName: string,
    category: string,
    [link:string]: string,
  }[]|undefined>(undefined);
  /**
   * Filter handling woith support for categories
   */
  const [showAll, onSetShowAll] = useState<boolean|{[category:string]:boolean}>(true);
  const [tableColumnRenderers, setTableColumnRenderers] = useState<TableColumns<UILicense>>({});
  const [additionalReady, setAdditionalReady] = useState<boolean>(false);

  const { conf } = useContext(UIConfiguration);
  const additionalDataUri = conf.functionality?.additionalLicenseDataURI;
  const dateFormat = conf.functionality?.["my-licenses"]?.["date-format"];
  /**
   * Incoming licenses as state object
   */
  const [slicenses, setLicenses] = useState<
      UILicense[] | { [category: string]: UILicense[]} |undefined
  >(undefined);
  useEffect(() => {
    const statusTips = {
      [UILicenseStatus.DISABLED]: intl.formatMessage({
        defaultMessage: "This license has been disabled",
        description: "tooltip for license status: disabled",
      }),
      [UILicenseStatus.EXPIRED]: intl.formatMessage({
        defaultMessage: "This license has expired",
        description: "tooltip for license status: expired",
      }),
      [UILicenseStatus.EXPIRING]: intl.formatMessage({
        defaultMessage: "This license is about to expire",
        description: "tooltip for license status: expiring",
      }),
      [UILicenseStatus.SCHEDULED]: intl.formatMessage({
        defaultMessage: "This license is scheduled and not yet active",
        description: "tooltip for license status: scheduled",
      }),
      [UILicenseStatus.VALID]: intl.formatMessage({
        defaultMessage: "This license is valid",
        description: "tooltip for license status: valid",
      }),
      [UILicenseStatus.INVALID]: intl.formatMessage({
        defaultMessage: "This license is invalid",
        description: "tooltip for license status: invalid",
      }),
    }
    const newR: TableColumns<UILicense> = {
      status: {
        renderLabel: (category?: string) =>
            <FormattedMessage
                defaultMessage="{category, select, other {Status}}"
                description="Status column header label. {category, select, other {Status}} prints 'Status', inject ' value {translated value} ' between the 'select,' and 'other' to translate & override label for specific category."
                values={{
                  category: category,
                }}
            />,
        render: (d) => <TooltipTrigger tipKey={"field-info"} tip={statusTips[d.status]}><span className={"status " + d.status}><span className={"status-label"}>{d.status}</span></span></TooltipTrigger>,
      },
      name: {
        renderLabel: (category?: string) =>
            <FormattedMessage
                defaultMessage="{category, select, other {Product name}}"
                description="Product name column header label. {category, select, other {Product name}} prints 'Product name', inject ' value {translated value} ' between the 'select,' and 'other' to translate & override label for specific category."
                values={{
                  category: category,
                }}
            />,
        render: (d) => <>{d.name}</>,
      },
      validFrom: {
        renderLabel: (category?: string) =>
          <FormattedMessage
              defaultMessage="{category, select, other {Valid from}}"
              description="valid from  column header label. {category, select, other {Valid from}} prints 'Valid from', inject ' value {translated value} ' between the 'select,' and 'other' to translate & override label for specific category."
              values={{
                category: category,
              }}
          />,
        render: (d) => <>{d.validFrom && d.validFrom.trim() !== '' && (!dateFormat || dateFormat === 'auto') ?
            intl.formatDate(d.validFrom) :
            (d.validFrom ? dayjs(new Date(d.validFrom as string)).format(dateFormat) : '')
        }</>,
      },
      validUntil: {
        renderLabel: (category?: string) =>
            <FormattedMessage
                defaultMessage="{category, select, other {Valid until}}"
                description="valid until  column header label. {category, select, other {Valid until}} prints 'Valid until', inject ' value {translated value} ' between the 'select,' and 'other' to translate & override label for specific category."
                values={{
                  category: category,
                }}
            />,
        render: (d) => {
          return <>{d.validUntil && d.validUntil.trim() !== '' && (!dateFormat || dateFormat === 'auto') ?
              intl.formatDate(d.validUntil) :
              (d.validUntil ? dayjs(new Date(d.validUntil as string)).format(dateFormat) : '')
          }</>;
        },
      },
    };
    if (additionalLicenseData) {
      newR.link= {
        renderLabel: (category?: string) =>
            <FormattedMessage
                defaultMessage="{category, select, Online {Software} other {Link}}"
                description="Link column header label. {category, select, other {Link}} prints 'Link', inject ' value {translated value} ' between the 'select,' and 'other' to translate & override label for specific category."
                values={{
                  category: category,
                }}
            />,
        render: (d) => {
          if (d.link?.length) {
            const t: string[] = d.link as string[];
            return <TooltipTrigger tipKey={"field-info"} tip={d.isValid ?
              <FormattedMessage
                  defaultMessage="{customtype, select, other {open}}"
                  description="Custom column link label. {customtype, select, other {open}} prints 'open', inject ' value {translated value} ' between the 'select,' and 'other' to translate label for customtype."
                  values={{
                    customtype: t[1],
                  }}
              /> : undefined
            }><a href={d.isValid ? t[0] : undefined} target={"_blank"} rel="noopener noreferrer"><span className={"link-label"}>
                <FormattedMessage
                  defaultMessage="{customtype, select, other {open}}"
                  description="Custom column link label. {customtype, select, other {open}} prints 'open', inject ' value {translated value} ' between the 'select,' and 'other' to translate label for customtype."
                  values={{
                    customtype: t[1],
                  }}
                />
              </span>
            </a></TooltipTrigger>
          } else {
            return <></>;
          }
        },
      };
    }
    setTableColumnRenderers(newR);
  }, [intl, additionalLicenseData, dateFormat])

  /**
   * Attempts to load additional license data
   */
  useEffect(() => {
    if (additionalDataUri) {
      if (!additionalLicenseData) {
        const t = !additionalDataUri.startsWith('/') && !additionalDataUri.startsWith('http')
            ? getAppBase() + '/' + additionalDataUri
            : additionalDataUri;
        // dev build renders twice causing a false alarm for double loading.
        fetch(t,
            {
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              }
            }
        )
            .then(function (response) {
              return response.json();
            })
            .then(function (myJson) {
              setAdditionalLicenseData(myJson);
            }).catch(() => {
        })
            .finally(() => {
              setAdditionalReady(true);
            });
      }
    } else {
      setAdditionalReady(true);
    }
  }, [additionalLicenseData,additionalDataUri]);

  useEffect(() => {
    if (isReady && !!userId && licenses === undefined) {
      onLoadLicenses(userId);
    }
  }, [isReady, userId, licenses, onLoadLicenses])

  const licensesRef = useRef(licenses);
  if (licensesRef.current === undefined && licenses === undefined) {
    licensesRef.current = licenses;
  } else if (!_.isEqual(licensesRef.current, licenses)) {
    licensesRef.current = licenses;
  }
  const licensesRefCurrent = licensesRef.current;
  /**
   * Updates state licenses when incoming licenses change, splits licenses into categories if in data.
   */
  useEffect(() => {
    if (licensesRefCurrent) {
      let newL:UILicense[] | { [category: string]: UILicense[]} |undefined = licensesRefCurrent
          .map((l, i) => licenseToUILicense(l, i, additionalLicenseData, conf?.functionality?.expiringInMs));
      if (additionalLicenseData) {
        licenseCategories = additionalLicenseData.map((a) => a.category)
        .filter((f,i,a) => a.indexOf(f) === i);
        const categorizedLic: {[category: string]: UILicense[]}= {};
        if (licenseCategories && licenseCategories.length) {
          const categorisedShowAll: {[category: string]: boolean} = {};
          licenseCategories?.forEach((c) => {
            if (categorizedLic) { // Why do we need this as this is initialized few lines above???
              categorizedLic[c] = (newL as UILicense[]).filter((f) => f.category === c);
            }
            categorisedShowAll[c] = true;
          });
          setLicenses(categorizedLic);
          onSetShowAll(categorisedShowAll);
        } else {
          setLicenses(newL);
        }
      } else {
        setLicenses(newL);
      }
    } else {
      setLicenses(undefined);
    }
  }, [intl, additionalLicenseData, licensesRefCurrent, conf?.functionality?.expiringInMs])
  /**
   * Filtered license state with category support
   */
  const [flicenses, setFilteredLicenses] = useState<
      UILicense[] | { [category: string]: UILicense[]} |undefined>(undefined);
  useEffect(() => {
    if (slicenses && slicenses.length) {
      setFilteredLicenses((slicenses as UILicense[]).filter((l) => showAll || l.isValid))
    } else if (slicenses) {
      const categories = Object.keys(slicenses);
      const filtered: {[category:string]: UILicense[]} = {};
      categories.forEach((c) => {
        filtered[c] = (slicenses as {[category:string]: UILicense[]})[c].filter((l) => (showAll as {[category:string]: boolean})[c] || l.isValid)
      })
      setFilteredLicenses(filtered)
    }
  }, [slicenses, showAll])

  return <View
      intl={intl}
      licenses={flicenses}
      userId={userId}
      onLoadLicenses={onLoadLicenses}
      isReady={isReady && additionalReady}
      showAll={showAll}
      onSetShowAll={onSetShowAll}
      columnRenderers={tableColumnRenderers}
      categories={licenseCategories}
      {...other} />
}
export default MyLicenses;
